import { lazy, Suspense } from "react";
import AuthGuard from "@/guards/auth-guard";
import GuestGuard from "@/guards/guest-guard";
import HomeLayout from "@/layouts/home-layout";
import { createBrowserRouter, Outlet } from "react-router";
import FullPageLoader from "@/components/loaders/full-page-loader";
import paths from "./paths";
import ErrorPage from "@/components/error-page";

const Home = lazy(() => import("@/features/home/pages"));
const Dashboard = lazy(() => import("@/features/dashboard/pages"));
const LeaderboardPage = lazy(() => import("@/features/leaderboard/pages"));
const LeaderboardUserDetailsPage = lazy(() => import("@/features/leaderboard/pages/user-details"));
const ReferralPage = lazy(() => import("@/features/referrals/pages"));
const ReferralHistoryPage = lazy(
  () => import("@/features/referrals/pages/history"),
);
const QuestsPage = lazy(() => import("@/features/quests/pages"));
const ProfilePage = lazy(() => import("@/features/user/pages/profile"));
const ItemsPage = lazy(() => import("@/features/user/pages/items"));
const RecentTransactionsPage = lazy(
  () => import("@/features/user/pages/recent-transactions"),
);
const EntrypassesPage = lazy(() => import("@/features/user/pages/entrypasses"));
const NftPage = lazy(() => import("@/features/nft/pages"));
const NftMarketplacePage = lazy(
  () => import("@/features/nft/pages/marketplace"),
);
const NftCartPage = lazy(() => import("@/features/nft/pages/cart"));
const QuestionsPage = lazy(() => import("@/features/questions/pages"));
const DailyTasksPage = lazy(() => import("@/features/daily-tasks/pages"));
const CryptoHunt = lazy(() => import("@/features/gaming/pages/crypto-hunt"));
const Jackpot = lazy(() => import("@/features/gaming/pages/jackpot"));
const PricePrediction = lazy(() => import("@/features/gaming/pages/price-prediction"));
const NftDetailPage = lazy(() => import("@/features/nft/pages/nft-detail"));
const NftCollectionsPage = lazy(
  () => import("@/features/nft/pages/nft-collection"),
);
const MyItemDetailPage = lazy(
  () => import("@/features/nft/pages/my-item-detail"),
);
const CustomizeNftPage = lazy(
  () => import("@/features/nft/pages/nft-customize"),
);
const AboutNftPage = lazy(() => import('@/features/nft/pages/about-nft'))
const MigrationPage = lazy(() => import("@/features/nft/pages/migration"));
const ItemDetailsPage = lazy(() => import('@/features/user/pages/items-details'));


const LazyWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<FullPageLoader />}>{children}</Suspense>
);

const routes = createBrowserRouter([
  {
    element: (
      <GuestGuard>
        <Outlet />
      </GuestGuard>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/",
        element: (
          <LazyWrapper>
            <Home />
          </LazyWrapper>
        ),
        index: true,
      },
    ],
  },
  {
    element: (
      <AuthGuard>
        <HomeLayout />
      </AuthGuard>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/dashboard",
        element: (
          <LazyWrapper>
            <Dashboard />
          </LazyWrapper>
        ),
      },
      {
        path: "/leaderboard",
        element: (
          <LazyWrapper>
            <LeaderboardPage />
          </LazyWrapper>
        ),
      },
      {
        path: "/leaderboard/:id",
        element: (
          <LazyWrapper>
            <LeaderboardUserDetailsPage />
          </LazyWrapper>
        ),
      },
      {
        path: "/referral",
        element: (
          <LazyWrapper>
            <ReferralPage />
          </LazyWrapper>
        ),
      },
      {
        path: "/referral/history",
        element: (
          <LazyWrapper>
            <ReferralHistoryPage />
          </LazyWrapper>
        ),
      },
      {
        path: "/quests",
        element: (
          <LazyWrapper>
            <QuestsPage />
          </LazyWrapper>
        ),
      },
      {
        path: "/user/profile",
        element: (
          <LazyWrapper>
            <ProfilePage />
          </LazyWrapper>
        ),
      },
      {
        path: "/user/items",
        element: (
          <LazyWrapper>
            <ItemsPage />
          </LazyWrapper>
        ),
      },
      {
        path: "/user/items/:id",
        element: (
          <LazyWrapper>
            <ItemDetailsPage />
          </LazyWrapper>
        ),
      },
      {
        path: "/user/transactions",
        element: (
          <LazyWrapper>
            <RecentTransactionsPage />
          </LazyWrapper>
        ),
      },
      {
        path: "/user/entrypasseses",
        element: (
          <LazyWrapper>
            <EntrypassesPage />
          </LazyWrapper>
        ),
      },
      {
        path: "/nft/home",
        element: (
          <LazyWrapper>
            <NftPage />
          </LazyWrapper>
        ),
      },
      {
        path: "/nft/marketplace",
        element: (
          <LazyWrapper>
            <NftMarketplacePage />
          </LazyWrapper>
        ),
      },
      {
        path: "/nft/cart",
        element: (
          <LazyWrapper>
            <NftCartPage />
          </LazyWrapper>
        ),
      },
      {
        path: "/nft/buy/:id",
        element: (
          <LazyWrapper>
            <NftDetailPage />
          </LazyWrapper>
        ),
      },
      {
        path: "/nft/my-items/:tokenId",
        element: (
          <LazyWrapper>
            <MyItemDetailPage />
          </LazyWrapper>
        ),
      },
      {
        path: "/nft/migration",
        element: (
          <LazyWrapper>
            <MigrationPage />
          </LazyWrapper>
        ),
      },
      {
        path: paths.nft.nftCollection,
        element: (
          <LazyWrapper>
            <NftCollectionsPage />
          </LazyWrapper>
        ),
      },
      {
        path: "/nft/customize/:level/:tokenId/:id",
        element: (
          <LazyWrapper>
            <CustomizeNftPage />
          </LazyWrapper>
        ),
      },

      {
        path: paths.nft.about, element: <LazyWrapper>
          <AboutNftPage />
        </LazyWrapper>
      },
      {
        path: "/nft/type/:id",
        element: (
          <LazyWrapper>
            <NftDetailPage />
          </LazyWrapper>
        ),
      },
      {
        path: "/questions",
        element: (
          <LazyWrapper>
            <QuestionsPage />
          </LazyWrapper>
        ),
      },
      {
        path: "/daily-tasks",
        element: (
          <LazyWrapper>
            <DailyTasksPage />
          </LazyWrapper>
        ),
      },
      {
        path: "/game/crypto-hunt",
        element: (
          <LazyWrapper>
            <CryptoHunt />
          </LazyWrapper>
        ),
      },
      {
        path: "/game/jackpot",
        element: (
          <LazyWrapper>
            <Jackpot />
          </LazyWrapper>
        ),
      },
      {
        path: "/game/price-prediction",
        element: (
          <LazyWrapper>
            <PricePrediction />
          </LazyWrapper>
        ),
      },
    ],
  },
]);

export { routes };
